import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/AdminShared.css";

const initialFormData = {
  businessName: "",
  businessSummary: "",
  bestServices: "",
  leadServices: "",
  serviceArea: "",
  idealCustomer: "",
  customerProblems: "",
  buyerObjections: "",
  proof: "",
  competitors: "",
  differentiators: "",
  currentWebsite: "",
  pagesNeeded: "",
  primaryCTA: "",
  goodLead: "",
  badLead: "",
  preferredColors: "",
  brandStyle: "",
  referenceSites: "",
  mustHaveContent: "",
};

const sections = [
  {
    title: "Business & Offer",
    fields: [
      ["businessName", "Business name", "input"],
      ["businessSummary", "Business summary", "textarea"],
      ["bestServices", "Most profitable services/products", "textarea"],
      ["leadServices", "Services/products they want more leads for", "textarea"],
      ["serviceArea", "Service area", "textarea"],
    ],
  },
  {
    title: "Customers & Positioning",
    fields: [
      ["idealCustomer", "Ideal customer", "textarea"],
      ["customerProblems", "Customer problems", "textarea"],
      ["buyerObjections", "Buyer objections", "textarea"],
      ["proof", "Proof/reviews/certifications/results", "textarea"],
      ["competitors", "Competitors", "textarea"],
      ["differentiators", "Differentiators", "textarea"],
    ],
  },
  {
    title: "Website Direction",
    fields: [
      ["currentWebsite", "Current website/social links", "textarea"],
      ["pagesNeeded", "Pages needed", "textarea"],
      ["primaryCTA", "Primary CTA", "input"],
      ["goodLead", "Good lead definition", "textarea"],
      ["badLead", "Bad fit definition", "textarea"],
    ],
  },
  {
    title: "Design Direction",
    fields: [
      ["preferredColors", "Preferred colors", "input"],
      ["brandStyle", "Brand style", "textarea"],
      ["referenceSites", "Reference websites", "textarea"],
      ["mustHaveContent", "Must-have content", "textarea"],
    ],
  },
];

const AdminGrowthIntake = () => {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState("Loading...");
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectSnap = await getDoc(doc(db, "projects", projectId));
        if (!projectSnap.exists()) throw new Error("Project not found.");

        const data = projectSnap.data();
        setProjectName(data.projectName || "Unnamed Project");
        setFormData({ ...initialFormData, ...(data.growthIntake || {}) });
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load Growth intake.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "projects", projectId), { growthIntake: formData });
      toast.success("Growth intake updated.");
      setError("");
    } catch (err) {
      setError("Failed to save intake: " + err.message);
      toast.error("Failed to save Growth intake.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ap-loading"><p>Loading Growth intake...</p></div>;

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}`} className="ap-back">← Back to project</a>
        <p className="ap-eyebrow">Admin — Growth</p>
        <h1 className="ap-title">{projectName}: Growth Intake</h1>
        <p className="ap-subtitle">Review the client's messaging, lead-generation, and design direction for the Growth package.</p>
      </div>

      {error && <p className="ap-message--error" style={{ fontFamily: "var(--font-ui)", fontSize: "0.9375rem" }}>{error}</p>}

      {sections.map((section) => (
        <div key={section.title} className="ap-section" style={{ marginBottom: "20px" }}>
          <div className="ap-section-header">
            <h2 className="ap-section-title">{section.title}</h2>
          </div>
          <div className="ap-section-body">
            {section.fields.map(([name, label, type]) => (
              <div key={name} className="ap-form-group">
                <label>{label}</label>
                {type === "input" ? (
                  <input
                    type="text"
                    value={formData[name]}
                    onChange={(event) => handleChange(name, event.target.value)}
                  />
                ) : (
                  <textarea
                    value={formData[name]}
                    onChange={(event) => handleChange(name, event.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="ap-btn-row">
        <button type="button" className="ap-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default AdminGrowthIntake;
